import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import {
  readMemberFromStorage,
  clearMemberSession,
  JAVILAND_MEMBER_AUTH_EVENT,
  getMemberFirstName
} from '../utils/memberSession';
import MemberAccountMenu from './MemberAccountMenu';
import './MemberAccountMenu.css';
import './Header.css';

const MOBILE_HEADER_BREAKPOINT = 768;

const Header = ({ onCommunityClick, onEcommerceClick }) => {
  const { t } = useTranslation();
  const { itemCount, toggleCart } = useCart();
  const [memberUser, setMemberUser] = useState(() => readMemberFromStorage());
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showTiendaMenu, setShowTiendaMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileTienda, setShowMobileTienda] = useState(false);
  const hideTimeoutRef = useRef(null);
  const menuTimeoutRef = useRef(null);

  useEffect(() => {
    const isNarrow = () => window.innerWidth <= MOBILE_HEADER_BREAKPOINT;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      }

      if (currentScrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [lastScrollY]);

  useEffect(() => {
    const syncMember = () => setMemberUser(readMemberFromStorage());
    syncMember();
    window.addEventListener('popstate', syncMember);
    window.addEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
    window.addEventListener('storage', syncMember);
    return () => {
      window.removeEventListener('popstate', syncMember);
      window.removeEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
      window.removeEventListener('storage', syncMember);
    };
  }, []);

  const memberFirstName = memberUser ? getMemberFirstName(memberUser) : '';
  const memberShortLabel =
    memberFirstName ||
    (memberUser?.email ? String(memberUser.email).split('@')[0] : '') ||
    '';

  const handleMemberLogout = () => {
    clearMemberSession();
    setMemberUser(null);
    setShowMobileMenu(false);
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goCommunity = () => {
    if (onCommunityClick) onCommunityClick();
    else navigateToPage('/comunidad');
  };

  return (
    <>
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${!isVisible ? 'hidden' : ''}`}>
      <div className="header-content">
        <div className="logo-container" onClick={() => navigateToPage('/')}>
          <img 
            src="/images/Logo_Javiland_Largo.png" 
            alt="Javiland Logo" 
            className="logo-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <span 
            className="logo-text fallback" 
            style={{ display: 'none' }}
          >
            JAVILAND
          </span>
        </div>
        
        <button 
          className="mobile-menu-toggle"
          onClick={() => {
            setShowMobileMenu(!showMobileMenu);
            setShowMobileTienda(false);
          }}
          aria-label="Toggle menu"
        >
          <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        <nav className="nav-menu-container">
          <ul className="nav-menu">
            <li><button className="nav-link" onClick={() => navigateToPage('/')}>{t('header.home')}</button></li>
            
            <li 
              className="nav-item-dropdown"
              onMouseEnter={() => {
                if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                setShowTiendaMenu(true);
              }}
              onMouseLeave={() => {
                menuTimeoutRef.current = setTimeout(() => setShowTiendaMenu(false), 200);
              }}
            >
              <button className="nav-link" onClick={() => navigateToPage('/tienda')}>
                {t('header.store')} <i className="fas fa-chevron-down"></i>
              </button>
              {showTiendaMenu && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda'); setShowTiendaMenu(false); }}>
                    Todos los productos
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=productos-naturales'); setShowTiendaMenu(false); }}>
                    {t('header.naturalProducts')}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateToPage('/tienda?category=combos'); setShowTiendaMenu(false); }}>
                    Combos bienestar
                  </button>
                </div>
              )}
            </li>

            <li>
              <button className="nav-link" onClick={() => navigateToPage('/comunidad')}>
                {t('header.community')}
              </button>
            </li>
          </ul>
        </nav>
        
        {showMobileMenu && (
          <nav className="mobile-menu">
            {memberUser && (
              <div className="mobile-member-banner">
                <button
                  type="button"
                  className="mobile-nav-link mobile-member-to-perfil-btn"
                  onClick={() => { navigateToPage('/perfil'); setShowMobileMenu(false); }}
                  title={memberUser.email || ''}
                >
                  {memberUser.avatar_data ? (
                    <img src={memberUser.avatar_data} alt="" className="mobile-member-btn-avatar" />
                  ) : (
                    <span className="mobile-member-btn-avatar mobile-member-btn-avatar--placeholder" aria-hidden>
                      <i className="fas fa-user" />
                    </span>
                  )}
                  <span className="mobile-member-btn-name">{memberShortLabel}</span>
                </button>
                <button type="button" className="mobile-nav-link mobile-member-logout" onClick={handleMemberLogout}>
                  {t('header.memberLogout')}
                </button>
              </div>
            )}
            <button className="mobile-nav-link" onClick={() => { navigateToPage('/'); setShowMobileMenu(false); }}>
              {t('header.home')}
            </button>
            <div className="mobile-nav-group">
              <button
                type="button"
                className="mobile-nav-link mobile-nav-expandable"
                onClick={() => setShowMobileTienda(!showMobileTienda)}
                aria-expanded={showMobileTienda}
              >
                {t('header.store')}{' '}
                <i className={`fas fa-chevron-down mobile-nav-chevron ${showMobileTienda ? 'open' : ''}`} />
              </button>
              {showMobileTienda && (
                <div className="mobile-nav-sublinks">
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    Todos los productos
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=productos-naturales');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    {t('header.naturalProducts')}
                  </button>
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage('/tienda?category=combos');
                      setShowMobileMenu(false);
                      setShowMobileTienda(false);
                    }}
                  >
                    Combos bienestar
                  </button>
                </div>
              )}
            </div>
            <button className="mobile-nav-link" onClick={() => { navigateToPage('/comunidad'); setShowMobileMenu(false); }}>
              {t('header.community')}
            </button>
            {!memberUser && (
              <button
                className="mobile-nav-link mobile-user-action"
                onClick={() => { goCommunity(); setShowMobileMenu(false); }}
              >
                <i className="fas fa-user-circle" /> Acceso
              </button>
            )}
          </nav>
        )}
        
        <div className="header-actions">
          {memberUser ? (
            <div className="header-member-session desktop-only" aria-live="polite">
              <MemberAccountMenu
                member={memberUser}
                onNavigate={navigateToPage}
                onLogout={handleMemberLogout}
              />
            </div>
          ) : (
            <button
              type="button"
              className="header-acceso-btn"
              onClick={goCommunity}
              title="Acceso comunidad Javiland"
            >
              <i className="fas fa-user-circle" aria-hidden />
              <span className="header-acceso-label">Acceso</span>
            </button>
          )}
          <button
            type="button"
            className="cart-icon"
            onClick={toggleCart}
            title={t('header.cart')}
            aria-label={t('header.cart')}
          >
            <i className="fas fa-shopping-cart"></i>
            {itemCount > 0 && (
              <span className="cart-badge">{itemCount}</span>
            )}
          </button>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
